/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import 'rc-dropdown/assets/index.css';
import Text from '../../../../shared/components/Text/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { setIsStaticAddColumnDropdownMenuOpen, setActiveModal } from '../../ducks/calendarActions';

const AddPropertyHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  cursor: pointer;
  color: #444444;
  width: 100%;
`;

const AddPropertyInputBox = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 32px;
  border: 1px dashed #4A90E2;
  border-radius: 4px;
  cursor: pointer;
  background: transparent;
  min-width: 160px;

  &:hover {
    background: rgba(74, 144, 226, 0.05);
  }
`;

const MenuWrapper = styled('div')`
  display: flex;
  overflow: hidden;
  flex-direction: column;
`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 4px 4px;
  background-color: #EEEEEE;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  width: 100%;
`;

const DropdownHeader = styled('div')`
  background-color: white;
  padding-bottom: 8px;
  display: flex;
  justify-content: space-between;
  padding: 7px;
`;

const ArrowDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuText = styled('div')`
  font-family: 'Nunito Sans';
  color: #444444;
  margin-bottom: 0px;
  font-size: 16px;
`;

const AddPropContainer = styled('div')`
  font-family: 'Nunito Sans';
  color: #444444;
  margin-bottom: 0px;
  font-size: 16px;
`;

// Create a new HoverableMenuItem component similar to CalendarItemDropdownOptions
const HoverableMenuItem = ({ item, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <MenuItem
      style={{
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 14,
        paddingRight: 7,
        cursor: 'pointer',
        background: isHovered ? '#F5F5F5' : 'transparent',
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IconContainer>
        {item.icon ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IcomoonReact
              iconSet={iconSet}
              size={18}
              icon={item.icon}
              color='#9E9E9E'
              style={{ marginRight: 5 }}
            />
            <MenuText>{item.label}</MenuText>
          </div>
        ) : (
          <ArrowDiv>
            <MenuText>{item.label}</MenuText>
          </ArrowDiv>
        )}
      </IconContainer>
    </MenuItem>
  );
};

HoverableMenuItem.propTypes = {
  item: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const capWords = (str) => str
  .split(' ')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  .join(' ');

const AddPropertyColumnComponent = ({
  mode,
  primaryRows,
  primaryRowSwitch,
  setPrimaryRowSwitch,
  setPrimaryRowSwitchState,
}) => {
  const dispatch = useDispatch();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const handleMenuItemClick = (row, e) => {
    e.domEvent.stopPropagation();
    const flippedStatus = !primaryRowSwitch[row.name];
    const newSwitch = { ...primaryRowSwitch };
    newSwitch[row.name] = flippedStatus;

    if (!flippedStatus) {
      setPrimaryRowSwitch(newSwitch);
    } else if (row.name === 'peak power' || row.name === 'velocity') {
      setPrimaryRowSwitch(newSwitch);
    } else {
      setPrimaryRowSwitchState(newSwitch);
    }
  };

  const handleAboutPropertiesClick = (e) => {
    dispatch(setActiveModal('aboutExercisePropertiesModal'));
  };

  const menu = (
    <Menu
      style={{
        width: 280,
        minHeight: 100,
        overflowY: 'auto',
        marginBottom: '0px',
        border: '1px solid #eeeeee',
      }}
    >
      <AddPropContainer onClick={(e) => e.stopPropagation()}>
        <DropdownHeader>
          <Text fontWeight={700} fontSize='16px'>
            Add Property
          </Text>
          <Text fontSize='16px' color='#9E9E9E'>
            ⌘+P
          </Text>
        </DropdownHeader>
      </AddPropContainer>
      <MenuWrapper style={{ gap: 4 }}>
        {primaryRows.map((primaryRow, index) => (
          !primaryRowSwitch[primaryRow.name] && primaryRow.name !== 'add-property' ? (
            <HoverableMenuItem
              key={`first-${primaryRow.name}-${index}`}
              item={{
                label: primaryRow.name === '%pr' ? '%PR' : capWords(primaryRow.name.includes('intensity') ? 'Intensity' : primaryRow.name),
              }}
              onClick={(e) => handleMenuItemClick(primaryRow, e)}
            />
          ) : null
        ))}
        <CustomDivider />
        <HoverableMenuItem
          key='about-properties'
          item={{
            label: 'About Properties',
            icon: 'info2',
          }}
          onClick={handleAboutPropertiesClick}
        />
      </MenuWrapper>
    </Menu>
  );

  const handleClick = (e) => {
    e.stopPropagation();
    setIsDropdownVisible(!isDropdownVisible);
  };

  return (
    <Dropdown
      trigger={['click']}
      overlay={menu}
      visible={isDropdownVisible}
      onVisibleChange={(visible) => {
        setIsDropdownVisible(visible);
        dispatch(setIsStaticAddColumnDropdownMenuOpen(visible));
      }}
    >
      <div
        style={{
          width: '100%',
        }}
        onClick={handleClick}
      >
        {mode === 'header' ? (
          <AddPropertyHeader>
            <span>Add Property</span>
            <IcomoonReact
              iconSet={iconSet}
              size={12}
              icon='down-arrow'
              color='#444444'
            />
          </AddPropertyHeader>
        ) : (
          <AddPropertyInputBox>
            <IcomoonReact
              iconSet={iconSet}
              size={16}
              icon='plus'
              color='#4A90E2'
            />
          </AddPropertyInputBox>
        )}
      </div>
    </Dropdown>
  );
};

AddPropertyColumnComponent.propTypes = {
  mode: PropTypes.oneOf(['header', 'input']).isRequired,
  primaryRows: PropTypes.instanceOf(Array).isRequired,
  primaryRowSwitch: PropTypes.instanceOf(Object).isRequired,
  setPrimaryRowSwitch: PropTypes.func.isRequired,
  setPrimaryRowSwitchState: PropTypes.func.isRequired,
};

export default AddPropertyColumnComponent;
